import React, { useState, useEffect, useRef } from 'react';
import './LetterSolitaire.css';
import Confetti from 'react-confetti';

// Create a deck of 52 letter cards (a–z twice), each with a unique id.
function createLetterDeck() {
  const alphabet = 'abcdefghijklmnopqrstuvwxyz';
  const deck = [];
  let idCounter = 0;
  for (let i = 0; i < 2; i++) {
    for (let char of alphabet) {
      deck.push({ letter: char, faceUp: false, id: `deck-${idCounter++}` });
    }
  }
  return deck;
}

// Shuffle using Fisher–Yates.
function shuffleDeck(deck) {
  for (let i = deck.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [deck[i], deck[j]] = [deck[j], deck[i]];
  }
  return deck;
}

// Deal cards into 7 columns (each column gets one more card than the previous).
function dealColumns(deck) {
  const columns = [[], [], [], [], [], [], []];
  let index = 0;
  for (let col = 0; col < 7; col++) {
    for (let row = 0; row <= col; row++) {
      columns[col].push({
        ...deck[index],
        faceUp: row === col, // only the top card is face–up
        id: `col${col}-card${row}-${deck[index].id}`,
      });
      index++;
    }
  }
  const remainingDeck = deck.slice(index);
  return { columns, remainingDeck };
}

function LetterSolitaire({ wordList, soundEffects }) {
  // Game state.
  const [deck, setDeck] = useState([]);
  // We use deckPointer as an integer in the range 0 … deck.length,
  // where deckPointer === deck.length means "blank" (no card shown).
  // In this model the top card is at index deck.length - 1.
  const [deckPointer, setDeckPointer] = useState(0);
  const deckPointerRef = useRef(0);
  const updateDeckPointer = (newPointer) => {
    setDeckPointer(newPointer);
    deckPointerRef.current = newPointer;
  };

  const [columns, setColumns] = useState([[], [], [], [], [], [], []]);
  const [foundations, setFoundations] = useState([[], [], [], [], []]);
  const [score, setScore] = useState(0);
  const [spelledWords, setSpelledWords] = useState([]);

  // Drag state.
  // For deck drags we store: { cards: [card], from: 'deck', draggedIndex }.
  const [draggedCards, setDraggedCards] = useState(null);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  // Save the original pointer so we can revert if needed.
  const [originalLocation, setOriginalLocation] = useState(null);

  // Saved game state (for check–word revert).
  const [savedState, setSavedState] = useState(null);
  const getCurrentGameState = () => ({
    deck: deck.map(card => ({ ...card })),
    columns: columns.map(col => col.map(card => ({ ...card }))),
    foundations: foundations.map(pile => pile.map(card => ({ ...card }))),
    deckPointer,
    score,
    spelledWords,
  });

  // Add new state for tracking game win
  const [hasWon, setHasWon] = useState(false);

  // Add new state to track if game is initialized
  const [isInitialized, setIsInitialized] = useState(false);

  // Add new state for error message
  const [errorMessage, setErrorMessage] = useState('');

  // Add function to check for win condition
  const checkWinCondition = () => {
    // Only win if:
    // 1. Game is initialized
    // 2. Deck is empty (length === 0)
    // 3. All columns are empty (every column has length === 0)
    // 4. Not already in won state
    if (isInitialized && 
        deck.length === 0 && 
        columns.every(col => col.length === 0) && 
        !hasWon) {
      setHasWon(true);
      if (soundEffects) soundEffects.play('gameWon');
    }
  };

  // Add useEffect to check win condition after every relevant state change
  useEffect(() => {
    // Only check win condition if game is initialized
    if (isInitialized) {
      checkWinCondition();
    }
  }, [deck, columns, isInitialized]);

  // INITIALIZATION: create, shuffle, and deal.
  useEffect(() => {
    let letterDeck = createLetterDeck();
    shuffleDeck(letterDeck);
    const { columns: dealCols, remainingDeck } = dealColumns(letterDeck);
    
    console.log('Initial deck:', remainingDeck);
    
    setColumns(dealCols);
    setDeck(remainingDeck);
    setFoundations([[], [], [], [], []]);
    setScore(0);
    setSpelledWords([]);
    
    const initPointer = remainingDeck.length > 0 ? remainingDeck.length - 1 : 0;
    updateDeckPointer(initPointer);
    setSavedState({
      deck: remainingDeck.map(card => ({ ...card })),
      columns: dealCols.map(col => col.map(card => ({ ...card }))),
      foundations: [[], [], [], [], []],
      deckPointer: initPointer,
      score: 0,
      spelledWords: [],
    });
    
    // Mark initialization as complete
    setIsInitialized(true);
  }, []);

  /* ---------------- DECK LOGIC ---------------- */
  // Clicking the deck–stack cycles the deck in reverse:
  // - If deckPointer is not blank (i.e. < deck.length) and > 0, then subtract 1.
  // - If deckPointer is 0, then set it to deck.length (blank).
  // - If deckPointer is blank (i.e. equals deck.length), then reset to deck.length - 1.
  const handleDeckCycle = (e) => {
    e.preventDefault();
    if (deck.length === 0) return;
    if (soundEffects) soundEffects.play('card');
    setDeckPointer(prev => {
      let newPointer;
      if (prev !== deck.length) {
        if (prev > 0) newPointer = prev - 1;
        else if (prev === 0) newPointer = deck.length; // blank
      } else {
        newPointer = deck.length - 1;
      }
      deckPointerRef.current = newPointer;
      return newPointer;
    });
  };

  /* ---------------- DRAG & DROP HANDLERS ---------------- */
  // When dragging a card from the deck, we use the current deckPointer (if not blank)
  // and then update the pointer in the opposite direction to clicking.
  // That is, we update by moving *forward* instead of backward.
  // Our new rule for dragging is:
  // - If deckPointer is not blank and is less than deck.length - 1, then new pointer = deckPointer + 1.
  // - If deckPointer equals deck.length - 1, then new pointer = 0.
  const handleTouchStartDeck = (e) => {
    e.preventDefault();
    const pointer = deckPointerRef.current;
    if (deck.length === 0 || pointer === deck.length) return; // No card to drag if blank.
    const draggedCard = deck[pointer];
    setDraggedCards({
      cards: [draggedCard],
      from: 'deck',
      draggedIndex: pointer, // record original pointer value
    });
    setOriginalLocation({ type: 'deck', pointer });
    setDragPosition({ x: e.touches[0].clientX, y: e.touches[0].clientY });
    // Update pointer using the *opposite* rule from clicking:
    // If pointer is less than deck.length - 1, then increment; if pointer is at deck.length - 1, wrap to 0.
    const newPointer = (pointer < deck.length - 1 ? pointer + 1 : 0);
    updateDeckPointer(newPointer);
    if (soundEffects) soundEffects.play('card');
  };

  const handleTouchStartColumn = (e, colIndex, cardIndex) => {
    e.preventDefault();
    const col = columns[colIndex];
    const card = col[cardIndex];
    if (!card.faceUp) return; // Cannot drag face–down cards.
    if (cardIndex !== col.length - 1) return; // Only top card is draggable.
    const cardSelector = `[data-col-index="${colIndex}"][data-card-id="${card.id}"]`;
    const cardEl = document.querySelector(cardSelector);
    if (cardEl) cardEl.classList.add('hidden-card');
    setDraggedCards({
      cards: [card],
      from: 'column',
      colIndex,
      cardIndex,
    });
    setOriginalLocation({ type: 'column', colIndex, cardIndex });
    setDragPosition({ x: e.touches[0].clientX, y: e.touches[0].clientY });
    if (soundEffects) soundEffects.play('card');
  };

  const handleTouchMove = (e) => {
    if (!draggedCards) return;
    e.preventDefault();
    const touch = e.touches[0];
    setDragPosition({ x: touch.clientX, y: touch.clientY });
  };

  // Attach a document-level touchend listener so that drops register immediately.
  useEffect(() => {
    const onDocTouchEnd = (e) => {
      if (draggedCards) {
        e.preventDefault();
        handleTouchEnd(e);
      }
    };
    document.addEventListener('touchend', onDocTouchEnd, { passive: false });
    return () => {
      document.removeEventListener('touchend', onDocTouchEnd);
    };
  }, [draggedCards, dragPosition]);

  const handleTouchEnd = (e) => {
    e.preventDefault();
    if (!draggedCards) return;
    const { x, y } = dragPosition;
    const fIndex = getFoundationIndexAt(x, y);
    if (fIndex !== -1) {
      handleFoundationDrop(fIndex);
    } else {
      // Drag canceled; revert changes.
      revertDraggedCards();
    }
    setDraggedCards(null);
  };

  const getFoundationIndexAt = (x, y) => {
    const foundEls = document.querySelectorAll('.foundation-pile');
    for (let i = 0; i < foundEls.length; i++) {
      const rect = foundEls[i].getBoundingClientRect();
      if (x >= rect.left && 
          x <= rect.right && 
          y >= rect.top && 
          y <= rect.bottom + 100) { // Extended drop area 100px below the foundation
        return i;
      }
    }
    return -1;
  };

  const getColumnIndexAt = (x, y) => {
    const colEls = document.querySelectorAll('.letter-column');
    for (let i = 0; i < colEls.length; i++) {
      const rect = colEls[i].getBoundingClientRect();
      if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
        return i;
      }
    }
    return -1;
  };

  // A foundation pile only accepts a card if it is empty.
  const isValidFoundationMove = (card, foundation) => foundation.length === 0;

  // When a card is dropped onto a foundation:
  // • For a deck card, remove it from the deck array and update the pointer using the dragging rule.
  // • For a column card, remove it from the column and flip the next card (if any).
  const handleFoundationDrop = (foundationIndex) => {
    if (!draggedCards) return;
    const card = draggedCards.cards[0];
    let newColumns = [...columns];
    let newDeck = [...deck];
    let newFoundations = [...foundations];
    if (isValidFoundationMove(card, newFoundations[foundationIndex])) {
      if (soundEffects) {
        soundEffects.play('card');
        soundEffects.play('goodMove');
      }
      if (draggedCards.from === 'deck') {
        const original = draggedCards.draggedIndex;
        // Remove the dragged card from the deck.
        newDeck.splice(original, 1);
        // Update pointer using the dragging rule:
        // If newDeck is empty, pointer becomes blank (i.e. newDeck.length);
        // Otherwise, if original is within bounds, pointer remains at original, else reset to 0.
        let newPointer;
        if (newDeck.length === 0) {
          newPointer = newDeck.length;
        } else {
          newPointer = (original < newDeck.length ? original : 0);
        }
        setDeck(newDeck);
        updateDeckPointer(newPointer);
      } else if (draggedCards.from === 'column') {
        newColumns[draggedCards.colIndex].pop();
        if (newColumns[draggedCards.colIndex].length > 0) {
          const topCard = newColumns[draggedCards.colIndex][newColumns[draggedCards.colIndex].length - 1];
          topCard.faceUp = true;
        }
        setColumns(newColumns);
      }
      newFoundations[foundationIndex].push(card);
      setFoundations(newFoundations);
    } else {
      // Invalid move – revert.
      revertDraggedCards();
    }
  };

  // Dropping onto a column is not allowed.
  const handleColumnDrop = (colIndex) => {
    revertDraggedCards();
    if (soundEffects) soundEffects.play('badMove');
  };

  // If a drag is canceled, revert the deck pointer (or column) to its original value
  // and remove any temporary CSS (e.g. "hidden-card").
  const revertDraggedCards = () => {
    if (!draggedCards) return;
    if (draggedCards.from === 'deck') {
      updateDeckPointer(originalLocation.pointer);
      const deckCardEl = document.querySelector('.deck-top.hidden-card');
      if (deckCardEl) deckCardEl.classList.remove('hidden-card');
    } else if (draggedCards.from === 'column') {
      const card = draggedCards.cards[0];
      const cardSelector = `[data-col-index="${draggedCards.colIndex}"][data-card-id="${card.id}"]`;
      const cardEl = document.querySelector(cardSelector);
      if (cardEl) cardEl.classList.remove('hidden-card');
    }
    if (soundEffects) {
      soundEffects.play('card');
      soundEffects.play('badMove');
    }
  };

  /* ---------------- CHECK WORD LOGIC ---------------- */
  // When the Check Word button is clicked, we read the letters from the foundation piles.
  // If the word is valid (exists in the word list), points are awarded, the foundations are cleared,
  // and the current game state is saved.
  // Otherwise, the state reverts to the saved state.
  const handleCheckWord = () => {
    const spelled = foundations.flatMap(pile => pile.map(c => c.letter)).join('');
    if (!spelled) {
      if (soundEffects) soundEffects.play('badMove');
      return;
    }

    // Check word length first
    if (spelled.length < 3) {
      if (soundEffects) soundEffects.play('badMove');
      setErrorMessage('Words must be at least 3 letters long');
      // Clear error message after 2 seconds
      setTimeout(() => setErrorMessage(''), 2000);
      // Revert to saved state
      if (savedState) {
        setDeck(savedState.deck.map(card => ({ ...card })));
        setColumns(savedState.columns.map(col => col.map(card => ({ ...card }))));
        setFoundations(savedState.foundations.map(pile => pile.map(card => ({ ...card }))));
        updateDeckPointer(savedState.deckPointer);
        setScore(savedState.score);
        setSpelledWords(savedState.spelledWords);
      }
      return;
    }

    if (wordList.includes(spelled.toLowerCase())) {
      if (soundEffects) {
        soundEffects.play('goodMove');
        soundEffects.play('card');
      }

      // New scoring logic
      let pointsEarned;
      switch (spelled.length) {
        case 3:
          pointsEarned = 3;
          break;
        case 4:
          pointsEarned = 8;
          break;
        default:  // 5 or more letters
          pointsEarned = 15;
          break;
      }

      const newScore = score + pointsEarned;
      setScore(newScore);
      setSpelledWords(prev => [...prev, spelled.toLowerCase()]);
      setFoundations([[], [], [], [], []]);
      const newState = getCurrentGameState();
      newState.foundations = [[], [], [], [], []];
      newState.score = newScore;
      newState.spelledWords = [...spelledWords, spelled.toLowerCase()];
      setSavedState(newState);

      // Update best score if needed
      const currentBest = localStorage.getItem('letterSolitaireBest') || 0;
      if (newScore > currentBest) {
        localStorage.setItem('letterSolitaireBest', newScore);
      }
    } else {
      if (savedState) {
        // When reverting, force new object references so that React sees a state change.
        setDeck(savedState.deck.map(card => ({ ...card })));
        setColumns(savedState.columns.map(col => col.map(card => ({ ...card }))));
        setFoundations(savedState.foundations.map(pile => pile.map(card => ({ ...card }))));
        updateDeckPointer(savedState.deckPointer);
        setScore(savedState.score);
        setSpelledWords(savedState.spelledWords);
      }
      if (soundEffects) soundEffects.play('badMove');
    }
  };

  return (
    <div className="lettersolitaire-container">
      {hasWon && (
        <>
          <Confetti />
          <div className="win-message">YOU WIN!</div>
          <div className="game-message">
            <button className="restart-button" onClick={() => window.location.reload()}>
              Play Again
            </button>
          </div>
        </>
      )}

      <div className="solitaire-topbar">
        <div className="score-area">
          <div className="solitaire-score">Score: {score}</div>
          <div className="solitaire-best">Best: {localStorage.getItem('letterSolitaireBest') || 0}</div>
        </div>
        <button onClick={handleCheckWord} className="check-word-btn">
          Check Word
        </button>
      </div>

      {/* Main game area */}
      <div className="game-area">
        <div className="foundations-row">
          {foundations.map((pile, i) => (
            <div 
              key={i} 
              className="foundation-pile"
              onTouchEnd={() => handleFoundationDrop(i)}
            >
              {pile.map((card, index) => (
                <div key={index} className="letter-card">
                  {card.letter.toUpperCase()}
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className="deck-area">
          {/* Left: Deck–stack; tapping it cycles the deck (reverse order). */}
          <div className="deck-stack" onTouchStart={handleDeckCycle}>
            {deck.length > 0 ? (
              <div className="letter-card face-down">
                {deck.length}
              </div>
            ) : (
              <div className="empty-deck">Empty</div>
            )}
          </div>
          {/* Right: Deck display.
              If deckPointer is within 0 … deck.length - 1, show that card;
              Otherwise, render a placeholder that is completely hidden. */}
          <div className="deck-display">
            {deckPointer < deck.length ? (
              <div 
                className="letter-card deck-top" 
                onTouchStart={handleTouchStartDeck}
                onTouchMove={handleTouchMove}
              >
                {deck[deckPointer].letter.toUpperCase()}
              </div>
            ) : (
              <div 
                className="deck-top blank" 
                style={{
                  width: 'calc(100vw / 7 - 12px)',
                  height: 'calc((100vw / 7 - 12px) * 1.4)',
                  visibility: 'hidden'
                }}
              ></div>
            )}
          </div>
        </div>

        <div className="columns">
          {columns.map((col, colIndex) => (
            <div key={colIndex} className="letter-column" onTouchEnd={() => handleColumnDrop(colIndex)}>
              {col.map((card, cardIndex) => (
                <div
                  key={card.id}
                  data-col-index={colIndex}
                  data-card-id={card.id}
                  className={`letter-card ${card.faceUp ? 'face-up' : 'face-down'}`}
                  onTouchStart={(e) => handleTouchStartColumn(e, colIndex, cardIndex)}
                  onTouchMove={handleTouchMove}
                >
                  {card.faceUp ? card.letter.toUpperCase() : '??'}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className="solitaire-foundwords">
        <h3>Found Words</h3>
        <ul className="word-list">
          {[...spelledWords].reverse().map((word, idx) => (
            <li key={idx}>{word.toUpperCase()}</li>
          ))}
        </ul>
      </div>

      {draggedCards && (
        <div 
          id="dragged-stack"
          style={{
            position: 'fixed',
            left: `${dragPosition.x - 20}px`,
            top: `${dragPosition.y - 70}px`,
            zIndex: 999,
            transform: 'translate(-50%, -50%)',
            touchAction: 'none',
          }}
          onTouchStart={(e) => e.stopPropagation()}
          onTouchMove={(e) => {
            e.preventDefault();
            handleTouchMove(e);
          }}
        >
          {draggedCards.cards.map((card, idx) => (
            <div 
              key={idx}
              className="letter-card dragging"
              style={{
                top: `${idx * 20}px`,
                position: 'absolute',
                width: 'calc(100vw / 7 - 12px)',
                height: 'calc((100vw / 7 - 12px) * 1.4)',
              }}
            >
              {card.letter.toUpperCase()}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default LetterSolitaire;
